import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Link, Pencil } from '@factorialco/factorial-one-react/icons/app'
import {
  MeetingAttendeeBaseFragment,
  MeetingBaseFragment,
  MeetingOrganizerFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formatDateToString, formatMeetingDuration } from 'modules/meetings/lib/utils/dates'

import { EditMeetingModal } from '../EditMeetingModal'

interface MeetingPageHeaderProps {
  meeting?: MeetingBaseFragment
  participants?: MeetingAttendeeBaseFragment[]
  organizer?: MeetingOrganizerFragment
}

const MeetingPageHeader = ({ meeting, participants, organizer }: MeetingPageHeaderProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    access: { locale },
    employee,
  } = useCurrentSession()

  const [openEditModal, setOpenEditModal] = useState(false)
  const showEditModal = employee?.id === organizer?.id

  const handleShareMeeting = async () => {
    try {
      const currentUrl = window.location.href
      await navigator.clipboard.writeText(currentUrl)

      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.meeting.page_header.link_copied'),
      })
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return (
    <>
      <ResourceHeader
        title={meeting?.title ?? '-'}
        primaryAction={{
          label: i18n.t('meetings.meeting.page_header.join_meeting'),
          onClick: () => {
            if (meeting?.id) {
              navigate(
                // TODO: change this with a hook that retrives the meeting type
                navigation.engagement.one_on_ones.meeting.meetId.prejoin.fullPath({
                  id: meeting?.id,
                })
              )
            }
          },
        }}
        secondaryActions={[
          {
            label: i18n.t('meetings.meeting.page_header.share'),
            icon: Link,
            onClick: handleShareMeeting,
          },
        ]}
        otherActions={
          (showEditModal && [
            {
              label: i18n.t('meetings.meeting.page_header.settings'),
              icon: Pencil,
              onClick: () => setOpenEditModal(true),
            },
          ]) ||
          []
        }
        metadata={[
          {
            label: i18n.t('meetings.meeting.page_header.organizer'),
            value: {
              type: 'avatar',
              text: organizer?.fullName ?? '',
              variant: {
                type: 'person',
                src: buildUrl(organizer?.avatar?.url),
                firstName: organizer?.firstName ?? '',
                lastName: organizer?.lastName ?? '',
              },
            },
          },
          {
            label: i18n.t('meetings.meeting.page_header.participants'),
            value: {
              avatars:
                participants?.map((participant) => ({
                  firstName: participant?.employee?.firstName ?? '',
                  lastName: participant?.employee?.lastName ?? '',
                  type: 'person',
                  src: buildUrl(participant?.employee?.avatar?.url),
                })) || [],
              type: 'list',
              variant: 'person',
            },
          },
          {
            label: i18n.t('meetings.meeting.page_header.date'),
            value: {
              type: 'text',
              content: meeting?.date ? formatDateToString(meeting?.date, locale) : '-',
            },
          },
          {
            label: i18n.t('meetings.meeting.page_header.time'),
            value: {
              type: 'text',
              content:
                meeting?.startTime && meeting?.endTime
                  ? formatMeetingDuration(meeting?.startTime, meeting?.endTime, locale)
                  : '-',
            },
          },
        ]}
      />
      {showEditModal && meeting && (
        <EditMeetingModal
          openEditModal={openEditModal}
          setOpenEditModal={setOpenEditModal}
          meeting={meeting}
        />
      )}
    </>
  )
}

export default MeetingPageHeader
